<template>
  <right-side-bar
    fixed
    divider
    prepend
    width="30%"
    header-style="mt-2 mr-2"
    :title="title"
    @toggle-right-bar="toggleDrawer"
    :is-right-drawer-open="isDrawerOpen"
  >
    <v-form
      ref="addSeedForm"
      id="add-seed-form"
      class="mt-2"
      v-model="isFormValid"
      @submit.prevent="onSubmitForm"
    >
      <div class="form-group" v-if="!isEditSeed">
        <v-text-field
          outlined
          class="required"
          label="Seed Address"
          autocomplete="email"
          aria-autocomplete="email"
          v-model="formData.seed_address"
          :rules="[validateEmail]"
        />
      </div>
      <div v-else id="seed-email-address">
        <div class="caption text--secondary pb-1">Seed Address</div>
        <div class="body-2 pb-6">{{ seedData.seed_address }}</div>
      </div>
      <div class="form-group pt-4">
        <password-input-field
          is-required
          label="Password"
          hide-details="auto"
          autocomplete="new-password"
          aria-autocomplete="new-password"
          :password="formData.password"
          @update-password="formData.password = $event"
        />
      </div>
      <v-divider class="mt-8 mb-6 border-light-grey" />
      <v-row no-gutters class="d-flex justify-start">
        <v-col cols="12">
          <v-btn
            type="submit"
            id="add-seed-submit-btn"
            medium
            color="primary"
            :loading="isLoading"
            :disabled="!isFormValid"
            class="px-4 button font-weight-bold white-btn-text"
          >
            Save Changes
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
  </right-side-bar>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { RightSideBar } from "@/components/drawers";
import { PasswordInputField } from "@/components/shared";

import { isEmpty, isResponseCode, pick } from "@/utils/common.utils";
import { validateEmail, required } from "@/validators";
import { createSeed, updateSeed } from "@/services/seed";
import { RESPONSE_CODES } from "@/constants";

/**
 * Add seed form
 */
export default {
  name: "AddSeedForm",
  /**
   * ---------------- Custom events ------------------
   */
  emits: ["toogle-drawer", "update-records"],
  /**
   * ---------------- Props ------------------
   */
  props: {
    isDrawerOpen: { type: Boolean, default: false, required: true },
    seedData: { type: Object, required: true, default: () => ({}) },
  },
  /**
   * ---------------- Components ------------------
   */
  components: {
    RightSideBar,
    PasswordInputField,
  },
  /**
   * ---------------- Data Properties ------------------
   */
  data() {
    const { snackbarTypes } = this.$appConfig.snackbar;

    return {
      isFormValid: false,
      isLoading: false,
      formData: { seed_address: "", password: "" },
      snackbarTypes,
    };
  },
  /**
   * ---------------- Methods ------------------
   */

  methods: {
    /**
     * Maps store actions in the component
     */
    ...mapActions({
      setSnackbar: "ui/setSnackbar",
      getMailboxProviders: "account/getMailboxProviders",
    }),
    validateEmail,
    required,
    /**
     * Toogles add or edit seed right drawer
     * @emits toogle-drawer
     */
    toggleDrawer(val) {
      this.$emit("toogle-drawer", val);
    },
    /**
     * Add seed form submit handler
     */
    onSubmitForm() {
      if (!this.isFormValid) return this.$refs.addSeedForm.validate();
      this.createUpdateSeed();
    },
    /**
     * Creates new seed
     * @description Send a post api call to create a new seed
     */
    async createUpdateSeed() {
      try {
        this.setIsLoading(true);
        let message = this.$dictionary.errors.common.msg;
        const { id: mailbox_provider_id } = this.$route.params;

        const { status } = !this.isEditSeed
          ? await createSeed({ ...this.formData, mailbox_provider_id })
          : await updateSeed(this.updateSeedPayload);

        // Shows success message if status of service is 200 else shows error message
        if (isResponseCode(status, RESPONSE_CODES.success)) {
          // Create and update seed success messages
          const { addSeedForm, editSeedForm } = this.$dictionary.app.seeds;

          message = !this.isEditSeed
            ? addSeedForm.successMessage
            : editSeedForm.successMessage;
        }

        this.setSnackbar({
          value: true,
          message,
          type: this.snackbarTypes.success,
        });
        this.toggleDrawer(false);
        this.$emit("update-records");
      } catch (error) {
        !this.snackbar.value &&
          this.setSnackbar({
            value: true,
            message: this.$errorMessage(error),
            type: this.snackbarTypes.error,
          });
      } finally {
        this.setIsLoading(false);
      }
    },
    /**
     * Updates the isLoading property
     */
    setIsLoading(val) {
      this.isLoading = val;
    },
  },
  /**
   * ---------------- Computed properties ------------------
   */
  computed: {
    ...mapGetters({
      mailboxProviders: "account/mailboxProviders",
      snackbar: "ui/snackbar",
    }),
    /**
     * Determines that seed is to be added or edited
     * @type {Boolean}
     */
    isEditSeed() {
      return this.seedData && !isEmpty(this.seedData);
    },
    /**
     * Title of add or edit right drawer
     * @type {String}
     */
    title() {
      const { addSeedForm, editSeedForm } = this.$dictionary.app.seeds;
      return this.isEditSeed ? editSeedForm.title : addSeedForm.title;
    },
    /**
     * Updat seed details payload
     */
    updateSeedPayload() {
      const props = ["password", "id"];
      const { id: mailbox_provider_id } = this.$route.params;

      return { ...pick(this.formData, props), mailbox_provider_id };
    },
  },
  /**
   * ---------------- Created lifecycle hook ------------------
   */
  created() {
    this.getMailboxProviders();

    if (this.isEditSeed) {
      this.formData = { ...this.seedData };
      this.formData.password = "";
    }
  },
};
</script>
